import { useState, useEffect } from 'react';
import { Download, Copy, Check, X, Loader2 } from 'lucide-react';
import { getSharedMediaByProduct, formatFileSize } from '../../services/sharedMediaService';
import { SHARED_MEDIA_CATEGORY_LABELS, type SharedMedia } from '../../types/sharedMedia';
import { logger } from '../../lib/logger';

interface ProductMarketingMaterialsProps {
  productId: string;
}

export function ProductMarketingMaterials({ productId }: ProductMarketingMaterialsProps) {
  const [media, setMedia] = useState<SharedMedia[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedMedia, setSelectedMedia] = useState<SharedMedia | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const loadMedia = async () => {
      setIsLoading(true);
      try {
        const data = await getSharedMediaByProduct(productId);
        setMedia(data);
      } catch (error) {
        logger.error('Error loading marketing materials:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadMedia();
  }, [productId]);

  const copyUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      logger.error('Copy error:', error);
    }
  };

  const downloadImage = (media: SharedMedia) => {
    const link = document.createElement('a');
    link.href = media.url;
    link.download = media.filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Don't render if no media
  if (!isLoading && media.length === 0) {
    return null;
  }

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-100 dark:border-slate-700 p-4">
      <h3 className="text-lg font-semibold text-[#1A1A1A] dark:text-white mb-4">
        Marketing-Materialien
      </h3>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 text-primary animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {media.map((item) => (
            <div
              key={item.id}
              className="relative group aspect-square rounded-lg overflow-hidden cursor-pointer border border-gray-100 dark:border-slate-700 hover:border-primary dark:hover:border-primary transition-colors"
              onClick={() => setSelectedMedia(item)}
            >
              <img
                src={item.url}
                alt={item.alt_text || item.filename}
                className="w-full h-full object-cover"
              />

              {/* Category Badge */}
              <div className="absolute bottom-1.5 left-1.5 px-1.5 py-0.5 bg-black/70 rounded text-[10px] text-white">
                {SHARED_MEDIA_CATEGORY_LABELS[item.category]}
              </div>

              {/* Hover Overlay */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 flex items-center gap-1 transition-opacity">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      downloadImage(item);
                    }}
                    className="p-1.5 bg-white/90 rounded-lg hover:bg-white transition-colors"
                    title="Download"
                  >
                    <Download className="w-3.5 h-3.5 text-gray-700" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      copyUrl(item.url);
                    }}
                    className="p-1.5 bg-white/90 rounded-lg hover:bg-white transition-colors"
                    title="URL kopieren"
                  >
                    {copied ? (
                      <Check className="w-3.5 h-3.5 text-green-500" />
                    ) : (
                      <Copy className="w-3.5 h-3.5 text-gray-700" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Preview Modal */}
      {selectedMedia && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm !mt-0">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-slate-700">
              <h4 className="font-semibold text-gray-900 dark:text-white truncate pr-2">
                {selectedMedia.filename}
              </h4>
              <button
                onClick={() => setSelectedMedia(null)}
                className="p-1 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-full transition-colors flex-shrink-0"
              >
                <X className="w-5 h-5 text-gray-500 dark:text-slate-400" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              <div className="aspect-video bg-gray-100 dark:bg-slate-700 flex items-center justify-center">
                <img
                  src={selectedMedia.url}
                  alt={selectedMedia.alt_text || selectedMedia.filename}
                  className="max-w-full max-h-full object-contain"
                />
              </div>

              <div className="p-4 space-y-3">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-gray-500 dark:text-slate-400 mb-1">Kategorie</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {SHARED_MEDIA_CATEGORY_LABELS[selectedMedia.category]}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500 dark:text-slate-400 mb-1">Dateigröße</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {formatFileSize(selectedMedia.size)}
                    </p>
                  </div>
                  {selectedMedia.width && selectedMedia.height && (
                    <div className="col-span-2">
                      <p className="text-gray-500 dark:text-slate-400 mb-1">Abmessungen</p>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {selectedMedia.width} × {selectedMedia.height} px
                      </p>
                    </div>
                  )}
                </div>

                <div>
                  <p className="text-gray-500 dark:text-slate-400 mb-1 text-sm">URL</p>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={selectedMedia.url}
                      readOnly
                      className="flex-1 px-3 py-2 text-sm bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg text-gray-600 dark:text-slate-300"
                    />
                    <button
                      onClick={() => copyUrl(selectedMedia.url)}
                      className="flex items-center gap-1.5 px-3 py-2 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-white rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors text-sm"
                    >
                      {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                      {copied ? 'Kopiert' : 'Kopieren'}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-4 py-3 border-t border-gray-100 dark:border-slate-700">
              <button
                onClick={() => downloadImage(selectedMedia)}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
              >
                <Download className="w-4 h-4" />
                Download
              </button>
              <button
                onClick={() => setSelectedMedia(null)}
                className="px-4 py-2 text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
